/**
 * TURNING `interests` INTO A RANKING
 * ============================================================
 * The recommendation quiz asks "What interests you?" and stores the answer on
 * the itinerary as `interests`. Nothing read it. The itinerary page filtered
 * listings by location and type, shuffled the result, and rendered the whole
 * list — so a traveller who picked Adventure and one who picked Relaxing got
 * the identical set of restaurants and activities, in the identical order.
 *
 * WHY A MAP AND NOT A DIRECT COMPARISON
 * -------------------------------------
 * The two vocabularies were never the same. Of the twelve `interests` values,
 * exactly two — `Spa` and `Luxury` — appear verbatim in the listing tag
 * vocabulary. `Adventure`, `Great Food`, `Hidden Gems` and the rest correspond
 * to nothing, so a naive `tags.includes(interest)` would score almost every
 * listing zero and change nothing.
 *
 * The tags below come from `service_provider_filters/categories.allowedTags`,
 * which an admin edits in the console. A tag named here that no longer exists
 * simply never matches — it costs nothing and breaks nothing. That tolerance is
 * deliberate: this map must never be the reason a listing stops appearing.
 *
 * SCORING IS ADDITIVE AND NEVER EXCLUDES
 * --------------------------------------
 * `rankByInterests` re-orders; it does not filter. A listing that matches no
 * interest still appears, just lower down. With six published listings in the
 * whole country, hiding anything would leave a traveller with an empty day —
 * and "we found nothing" is a worse answer than "here is what there is".
 * ============================================================
 */

/**
 * Interest value (as stored on the itinerary) → listing tags that satisfy it.
 *
 * Keyed on `value`, not the label the traveller reads: `Luxury` is shown as
 * "Private Island" and `wildlife` is shown as "Wildlife".
 */
export const INTEREST_TAGS = {
  'Must-see': ['Scenic Views', 'Photography Tours', 'Cultural Tours'],
  Culture: ['Cultural Tours', 'Village Visits'],
  Outdoors: ['Hiking', 'Bush Walk', 'Cycling', 'Birdwatching', 'Kayaking / Canoeing', 'Horse Riding'],
  Golf: ['Golf Course'],
  wildlife: ['Game Drive', 'Bush Walk', 'Birdwatching', 'Mokoro (Dugout Canoe)'],
  Spa: ['Spa', 'Pool'],
  'Great Food': ['Restaurant On-Site', 'Bar'],
  'Wine & Beer': ['Bar'],
  Adventure: [
    'White Water Rafting',
    'Bungee Jumping',
    'Zip Lining',
    'Jet Boat Rides',
    'Helicopter Rides',
    'Kayaking / Canoeing',
  ],
  Relaxing: ['Pool', 'Spa', 'Sunset Views', 'Scenic Views', 'Adults Only'],
  'Hidden Gems': ['Private / Exclusive', 'Eco-Friendly', 'Sustainable Tourism'],
  Luxury: ['Luxury', 'Private Island', 'Overwater', 'Private / Exclusive'],
};

/** The tags a set of interests is looking for, deduped. */
export function tagsForInterests(interests) {
  const wanted = new Set();
  (Array.isArray(interests) ? interests : []).forEach((interest) => {
    (INTEREST_TAGS[interest] || []).forEach((tag) => wanted.add(tag));
  });
  return wanted;
}

/**
 * How well one listing answers the traveller's interests: the number of its
 * tags that any chosen interest asked for.
 */
export function scoreListing(listing, wantedTags) {
  if (!wantedTags || wantedTags.size === 0) return 0;
  const tags = Array.isArray(listing?.tags) ? listing.tags : [];
  let score = 0;
  tags.forEach((tag) => {
    if (wantedTags.has(tag)) score += 1;
  });
  return score;
}

/**
 * Order listings for one day: best match first, varying which of the equally
 * good ones leads.
 *
 * `dayIndex` rotates WITHIN a score band rather than across the whole list, so
 * day three still opens with something the traveller said they wanted — it is
 * just not the same thing day one opened with. The old code shuffled instead,
 * which produced neither relevance nor, as it turned out, variety: `shuffle`
 * mutates and returns its argument, so every day and every time slot held a
 * reference to one array and rendered one identical order.
 *
 * Pure — the input array is never mutated.
 */
export function rankByInterests(listings, interests, dayIndex = 0) {
  const source = Array.isArray(listings) ? listings : [];
  if (source.length === 0) return [];

  const wanted = tagsForInterests(interests);

  const bands = new Map();
  source.forEach((listing) => {
    const score = scoreListing(listing, wanted);
    if (!bands.has(score)) bands.set(score, []);
    bands.get(score).push(listing);
  });

  const rotate = (items) => {
    if (items.length < 2) return items;
    const by = ((dayIndex % items.length) + items.length) % items.length;
    return [...items.slice(by), ...items.slice(0, by)];
  };

  return [...bands.keys()]
    .sort((a, b) => b - a)
    .flatMap((score) => rotate(bands.get(score)));
}
